import { encodeWav } from '../wav';

export type RecorderHandle = {
  write(samples: Int16Array): Promise<void>;
  close(): Promise<void>;
  exportWav(): Promise<Blob>;
  exportRaw(): Promise<ArrayBuffer>;
};

export async function createOpfsRecorder(filename: string): Promise<RecorderHandle> {
  const root = await navigator.storage.getDirectory();
  const file = await root.getFileHandle(filename, { create: true });
  const stream = await (file as unknown as { createWritable: () => Promise<FileSystemWritableFileStream> }).createWritable();

  // Writes are fired and forgotten from the packet callback (`void write(...)`),
  // and close() is called from a synchronous stop(). Without serialisation,
  // close() could run before the last writes land (truncation) and exportWav()
  // could read the file while the writable still holds its exclusive lock (empty
  // or failed download). So we chain every write, make close() wait for the
  // whole chain, and make exportWav() wait for close() — the file is then
  // guaranteed flushed and unlocked before we read it.
  let chain: Promise<void> = Promise.resolve();
  let closed: Promise<void> | null = null;

  function close(): Promise<void> {
    if (!closed) closed = chain.then(() => stream.close());
    return closed;
  }

  return {
    write(samples) {
      chain = chain.then(() => stream.write(samples.buffer as ArrayBuffer));
      return chain;
    },
    close,
    async exportWav() {
      await close();
      const f = await file.getFile();
      const pcm = await f.arrayBuffer();
      return new Blob([encodeWav(new Int16Array(pcm), 48000)], { type: 'audio/wav' });
    },
    async exportRaw() {
      await close();
      const f = await file.getFile();
      return f.arrayBuffer(); // rohe Int16-LE-Bytes, kein WAV-Header
    },
  };
}

export async function readOpfsRaw(filename: string): Promise<ArrayBuffer | null> {
  try {
    const root = await navigator.storage.getDirectory();
    const handle = await root.getFileHandle(filename);
    return await (await handle.getFile()).arrayBuffer();
  } catch { return null; }
}
